interface Package {
  id: number;
  country_id: number;
  name: string;
  name_ar?: string;
  description: string;
  description_ar?: string;
  price: number;
  currency: string;
  features: string[];
  features_ar?: string[];
  processing_time: string;
  processing_time_ar?: string;
  popular: boolean;
  active: boolean;
  sort_order: number;
  country_name: string;
  country_code: string;
  country_currency: string;
  country_slug?: string;
}

interface Country {
  id: number;
  name: string;
  name_ar?: string;
  code: string;
  currency: string;
  flag_emoji?: string;
  active: boolean;
  slug?: string;
}

class PackageService {
  private baseUrl = '/backend/api';
  private countriesCache: Country[] | null = null;

  async getPackages(): Promise<Package[]> {
    try {
      const response = await fetch(`${this.baseUrl}/packages/`);
      const data = await response.json();
      return data.packages || [];
    } catch (error) {
      return [];
    }
  }

  async getPackagesByCountry(countryCode: string): Promise<Package[]> {
    try {
      const packages = await this.getPackages();
      return packages.filter(pkg =>
        pkg.country_code.toLowerCase() === countryCode.toLowerCase() &&
        pkg.active
      ).sort((a, b) => a.sort_order - b.sort_order);
    } catch (error) {
      return [];
    }
  }

  async getCountries(): Promise<Country[]> {
    try {
      if (this.countriesCache) {
        return this.countriesCache;
      }

      const response = await fetch(`${this.baseUrl}/countries/`);
      const data = await response.json();
      const countries = data.countries || [];
      this.countriesCache = countries;
      return countries;
    } catch (error) {
      return [];
    }
  }

  // Format price for display
  formatPrice(package_: Package): string {
    if (package_.price === 0) return 'Free';

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: package_.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    try {
      return formatter.format(package_.price);
    } catch {
      // Fallback if currency is not recognized
      return `${package_.currency} ${package_.price.toLocaleString()}`;
    }
  }

  // Get all packages grouped by slug format (dynamically from API)
  async getAllPackagesGroupedBySlug(): Promise<Record<string, Package[]>> {
    try {
      const allPackages = await this.getPackages();
      const grouped: Record<string, Package[]> = {};

      allPackages.forEach(pkg => {
        // Use the slug from the API (country_slug field)
        const slug = pkg.country_slug;

        // Skip packages without a slug (countries without service pages)
        if (!slug) {
          return;
        }

        if (!grouped[slug]) {
          grouped[slug] = [];
        }

        if (pkg.active) {
          grouped[slug].push(pkg);
        }
      });

      // Sort packages in each group
      Object.keys(grouped).forEach(slug => {
        grouped[slug].sort((a, b) => a.sort_order - b.sort_order);
      });

      return grouped;
    } catch (error) {
      return {};
    }
  }
}

const packageServiceInstance = new PackageService();
export default packageServiceInstance;
export type { Package, Country };